import React, { lazy, Suspense } from "react";
import { BrowserRouter, Routes as RouterRoutes, Route } from "react-router-dom";
import ScrollToTop from "./components/ScrollToTop";
import ErrorBoundary from "./components/ErrorBoundary";
import NotFound from "./pages/NotFound";

// ✅ PUBLIC PAGES (Marketing Site - No Auth Required)
import LandingPage from './pages/public/LandingPage';
import HowItWorks from './pages/public/HowItWorks';
import Pricing from './pages/public/Pricing';
import CompareToolsPage from './pages/public/ComparePage';  // Renamed to avoid conflict
import AboutPage from './pages/public/AboutPage';
import AviationHub from './pages/public/AviationHub';
import PrivacyPolicy from './pages/public/PrivacyPolicy';
import TermsOfService from './pages/public/TermsOfService';
import ContactPage from './pages/public/ContactPage';
import DataPrivacyPage from './pages/public/DataPrivacyPage'; // ✅ NEW: Data usage explanation

// ✅ AUTHENTICATED PAGES (App - Auth Required)
import Settings from './pages/settings';
import Dashboard from './pages/dashboard';
import DashboardV2 from './pages/dashboardv2';  // ✅ NEW: Apple-inspired minimal dashboard
import TimecardDetail from './pages/timecard-detail';
import Calendar from './pages/calendar';
import CurrentTripView from './pages/current-trip-view'; // ✅ UPDATED: New mobile-first widget
import SyncHistory from './pages/sync-history';
import Login from './pages/auth/Login';
import Signup from './pages/auth/Signup';
import AuthCallback from './pages/auth/AuthCallback';
import DataManagement from './pages/data-management';
import Onboarding from './pages/onboarding';
import RotationDetailV3 from './pages/rotation-detail-v3';  // ✅ V3 (BADASS DESIGN)
import RotationBreakdown from './pages/rotation-breakdown';  // ✅ NEW: Clean redesign with Path A
import AuditPage from './pages/audit';  // ✅ NEW: Version tracking & linking visualization
import AuditLanding from './pages/audit-landing';  // ✅ NEW: Split-view audit landing page (rotations + timecards)
import ComparePage from './pages/compare';  // ✅ Version comparison page (app feature)
import RerouteLab from './pages/reroute-lab';
import RerouteSimulator from './pages/reroute-simulator';  // ✅ NEW: What-if simulator for testing reroutes
import GraphicsWireframe from './pages/graphics';
import ShareMyFlights from './pages/share-my-flights';  // ✅ NEW: Family flight tracking (Where Is My Pilot)

// Lazy-loaded pages
const Spillover = lazy(() => import('./pages/spillover'));

// ✅ CLEANED UP: Removed duplicate pages (analytics, rotations-list, compliance, events, flights, audit-log)
// These are now handled by:
// - Analytics → Dashboard charts
// - Rotations List → Data Management
// - Compliance → Rotation Detail Tab 3 + Current Trip View
// - Events → Data Management
// - Flights → Current Trip View
// - Audit Log → To be rebuilt with Rules Integration

const Routes = () => {
  return (
    <BrowserRouter>
      <ErrorBoundary>
      <ScrollToTop />
      <RouterRoutes>
        {/* ============================================ */}
        {/* PUBLIC PAGES (Marketing Site)               */}
        {/* ============================================ */}
        <Route path="/" element={<LandingPage />} />
        <Route path="/how-it-works" element={<HowItWorks />} />
        <Route path="/pricing" element={<Pricing />} />
        <Route path="/compare-tools" element={<CompareToolsPage />} />  {/* /compare-tools to avoid conflict with /compare */}
        <Route path="/about" element={<AboutPage />} />
        <Route path="/aviation-hub" element={<AviationHub />} />
        <Route path="/data-privacy" element={<DataPrivacyPage />} />  {/* ✅ NEW: Data usage explanation */}
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/terms" element={<TermsOfService />} />
        <Route path="/contact" element={<ContactPage />} />

        {/* ============================================ */}
        {/* AUTH PAGES                                  */}
        {/* ============================================ */}
        <Route path="/login" element={<Login />} />
        <Route path="/signup" element={<Signup />} />
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route path="/onboarding" element={<Onboarding />} />

        {/* ============================================ */}
        {/* AUTHENTICATED APP PAGES                     */}
        {/* ============================================ */}
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/dashboardv2" element={<DashboardV2 />} /> {/* ✅ NEW: Apple-inspired minimal dashboard */}
        <Route path="/settings" element={<Settings />} />
        <Route path="/sync-history" element={<SyncHistory />} />
        <Route path="/calendar" element={<Calendar />} />
        <Route path="/rotation-detail" element={<RotationDetailV3 />} /> {/* ✅ V3 is now default */}
        <Route path="/rotation-breakdown" element={<RotationBreakdown />} /> {/* ✅ NEW: Clean redesign with Option A + Path A */}
        <Route path="/timecard-detail" element={<TimecardDetail />} />
        <Route path="/data-management" element={<DataManagement />} />
        <Route path="/current-trip-view" element={<CurrentTripView />} />
        <Route path="/audit-landing" element={<AuditLanding />} /> {/* ✅ NEW: Split-view audit landing (rotations + timecards) */}
        <Route path="/audit" element={<AuditPage />} /> {/* ✅ NEW: Version audit page (query params: ?type=rotation&number=3599) */}
        <Route path="/audit/:entityType/:entityId" element={<AuditPage />} /> {/* ✅ NEW: Direct version ID path */}
        <Route path="/reroute-lab" element={<RerouteLab />} /> {/* ✅ Reroute detection and §23L hours-only view */}
        <Route path="/reroute-simulator" element={<RerouteSimulator />} /> {/* ✅ NEW: What-if simulator for testing reroute scenarios */}
        <Route path="/compare" element={<ComparePage />} /> {/* ✅ Version comparison (app feature) */}
        <Route path="/graphics" element={<GraphicsWireframe />} /> {/* ✅ Graphics wireframe page for A/B & merged views */}
        <Route path="/share-my-flights" element={<ShareMyFlights />} /> {/* ✅ NEW: Family flight tracking (Where Is My Pilot) */}
        <Route path="/spillover" element={<Suspense fallback={<div>Loading...</div>}><Spillover /></Suspense>} />

        {/* ============================================ */}
        {/* 404 NOT FOUND                               */}
        {/* ============================================ */}
        <Route path="*" element={<NotFound />} />
      </RouterRoutes>
      </ErrorBoundary>
    </BrowserRouter>
  );
};

export default Routes;